import { useState, useEffect } from 'react';
import {
  Flex,
  View,
  Heading,
  Text,
  Button,
  IconButton,
  TextInput,
  SimpleSelect,
  Popover,
  Alert,
  Spinner,
} from '@smarterservices/smarter-ui';
import { IconTrashLine, IconEditLine, IconAddLine, IconInfoLine, IconLinkLine, IconCopyLine } from '@instructure/ui';
import { useExamSessionStore, useAppStore, getStore } from '../../stores/StoreContext';

const Sharing = () => {
  const sessionSid = useExamSessionStore((state) => state.sessionSid);
  const examSid = useExamSessionStore((state) => state.examSid);
  const installSid = useExamSessionStore((state) => state.installSid);
  const user = useAppStore((state) => state.user);
  const AppStore = getStore('appStore');

  const [shares, setShares] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const [isCreating, setIsCreating] = useState(false);
  const [editingShareSid, setEditingShareSid] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    comments: '',
    emails: '',
    expiresIn: '7',
  });

  const [magicLinkShare, setMagicLinkShare] = useState(null);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkTtl, setMagicLinkTtl] = useState('60');
  const [magicLinkUrl, setMagicLinkUrl] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState(null);
  const [magicLinkCopied, setMagicLinkCopied] = useState(false);

  // Load shares when all required SIDs are available
  useEffect(() => {
    if (sessionSid && examSid && installSid) {
      loadShares();
    }
  }, [sessionSid, examSid, installSid]);

  const loadShares = async () => {
    setIsLoading(true);
    setError(null);

    const result = await AppStore.ApiClient.getSessionShares({
      installSid,
      examSid,
      sessionSid,
    });

    if (result.successful) {
      setShares(result.data.results || []);
    } else {
      setError('Failed to load shares');
    }

    setIsLoading(false);
  };

  const calculateExpires = (daysFromNow) => {
    if (daysFromNow === 'never') return new Date('2070-01-01T00:00:00.000Z').toISOString();
    const date = new Date();
    date.setDate(date.getDate() + parseInt(daysFromNow));
    return date.toISOString();
  };

  const handleCreate = () => {
    setIsCreating(true);
    setError(null);
    setFormData({ name: '', comments: '', emails: '', expiresIn: '7' });
  };

  const handleEdit = (share) => {
    setEditingShareSid(share.sid);
    setError(null);
    setFormData({
      name: share.name,
      comments: share.comments || '',
      emails: share.emails.join(', '),
      expiresIn: share.expires ? '7' : 'never',
    });
  };

  const handleDelete = async (shareSid) => {
    if (!confirm('Are you sure you want to delete this share?')) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    const result = await AppStore.ApiClient.deleteSessionShare({
      installSid,
      examSid,
      sessionSid,
      shareSid,
      payload: {
        deletedBySid: user?.sid,
      },
    });

    if (result.successful) {
      await loadShares();
      setSuccessMessage('Share deleted successfully. Recipients will no longer have access.');
    } else {
      setError('Failed to delete share. Please try again.');
    }

    setIsSaving(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.emails.trim()) {
      setError('At least one email is required');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    const emailArray = formData.emails
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);

    const payload = {
      name: formData.name,
      comments: formData.comments,
      emails: emailArray,
      expires: calculateExpires(formData.expiresIn),
    };

    let result;
    if (editingShareSid) {
      result = await AppStore.ApiClient.updateSessionShare({
        installSid,
        examSid,
        sessionSid,
        shareSid: editingShareSid,
        payload,
      });
    } else {
      result = await AppStore.ApiClient.createSessionShare({
        installSid,
        examSid,
        sessionSid,
        payload: {
          ...payload,
          createdBySid: user?.sid,
          createdByEmail: user?.email,
        },
      });
    }

    if (result.successful) {
      await loadShares();
      setIsCreating(false);
      setSuccessMessage(
        editingShareSid
          ? 'Share updated successfully.'
          : 'Share created successfully. Recipients have been notified by email.'
      );
      setEditingShareSid(null);
      setFormData({ name: '', comments: '', emails: '', expiresIn: '7' });
    } else {
      setError(
        editingShareSid ? 'Failed to update share. Please try again.' : 'Failed to create share. Please try again.'
      );
    }

    setIsSaving(false);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingShareSid(null);
    setError(null);
    setFormData({ name: '', comments: '', emails: '', expiresIn: '7' });
  };

  const showForm = isCreating || editingShareSid;

  const handleOpenMagicLink = (share) => {
    setMagicLinkShare(share);
    setMagicLinkEmail(share.emails[0] || '');
    setMagicLinkTtl('60');
    setMagicLinkUrl('');
    setMagicLinkError(null);
    setMagicLinkCopied(false);
  };

  const handleCloseMagicLink = () => {
    setMagicLinkShare(null);
    setMagicLinkUrl('');
    setMagicLinkError(null);
    setMagicLinkCopied(false);
  };

  const handleGenerateMagicLink = async () => {
    if (!magicLinkEmail) {
      setMagicLinkError('Please select a recipient.');
      return;
    }

    setIsGeneratingLink(true);
    setMagicLinkError(null);
    setMagicLinkUrl('');
    setMagicLinkCopied(false);

    const result = await AppStore.ApiClient.generateMagicLink({
      installSid,
      examSid,
      sessionSid,
      payload: {
        type: 'share',
        ttl: parseInt(magicLinkTtl),
        sid: magicLinkShare.sid,
        metadata: {
          email: magicLinkEmail,
        },
      },
    });

    if (result.successful) {
      setMagicLinkUrl(result.data?.magicLink || result.data);
    } else {
      setMagicLinkError('Failed to generate magic link. Please try again.');
    }

    setIsGeneratingLink(false);
  };

  const handleCopyMagicLink = async () => {
    try {
      await navigator.clipboard.writeText(magicLinkUrl);
      setMagicLinkCopied(true);
      setTimeout(() => setMagicLinkCopied(false), 3000);
    } catch {
      setMagicLinkError('Failed to copy to clipboard.');
    }
  };

  if (isLoading) {
    return (
      <View as="div" padding="large" textAlign="center">
        <Spinner renderTitle="Loading shares..." />
      </View>
    );
  }

  return (
    <View as="div" padding="small">
      <Flex direction="column" gap="small">
        {successMessage && (
          <Flex.Item>
            <Alert
              variant="success"
              margin="none none small none"
              renderCloseButtonLabel="Close"
              onDismiss={() => setSuccessMessage(null)}
            >
              {successMessage}
            </Alert>
          </Flex.Item>
        )}

        {error && (
          <Flex.Item>
            <Alert
              variant="error"
              margin="none none small none"
              renderCloseButtonLabel="Close"
              onDismiss={() => setError(null)}
            >
              {error}
            </Alert>
          </Flex.Item>
        )}

        <Flex.Item>
          <Flex justifyItems="space-between" alignItems="center">
            <Flex alignItems="center" gap="x-small">
              <Heading level="h3">Session Sharing</Heading>
              <Popover
                on="click"
                screenReaderLabel="Sharing information"
                renderTrigger={
                  <IconButton
                    renderIcon={IconInfoLine}
                    screenReaderLabel="Sharing information"
                    size="small"
                    withBackground={false}
                    withBorder={false}
                  />
                }
              >
                <View as="div" padding="small" maxWidth="300px">
                  <Text size="small">
                    Shared users will have access to view this session based on
                    the share configuration. Each share includes a unique link
                    that will be sent to the specified email addresses.
                  </Text>
                </View>
              </Popover>
            </Flex>
            {!showForm && shares.length > 0 && (
              <Button
                renderIcon={IconAddLine}
                onClick={handleCreate}
                color="primary"
                disabled={isSaving}
              >
                Create Share
              </Button>
            )}
          </Flex>
        </Flex.Item>

        {showForm && (
          <Flex.Item>
            <View
              as="div"
              background="secondary"
              padding="small"
              borderRadius="medium"
            >
              <Flex direction="column" gap="small">
                <Heading level="h4">
                  {editingShareSid ? 'Edit Share' : 'Create New Share'}
                </Heading>

                <TextInput
                  renderLabel="Share Name"
                  value={formData.name}
                  onChange={(e, value) =>
                    setFormData({ ...formData, name: value })
                  }
                  placeholder="My Share"
                  isRequired
                  disabled={isSaving}
                />

                <TextInput
                  renderLabel="Email Addresses (comma-separated)"
                  value={formData.emails}
                  onChange={(e, value) =>
                    setFormData({ ...formData, emails: value })
                  }
                  placeholder="instructor1@example.com, instructor2@example.com"
                  isRequired
                  disabled={isSaving}
                />

                <TextInput
                  renderLabel="Comments (optional)"
                  value={formData.comments}
                  onChange={(e, value) =>
                    setFormData({ ...formData, comments: value })
                  }
                  placeholder="Additional notes about this share"
                  disabled={isSaving}
                />

                <SimpleSelect
                  renderLabel="Expires In"
                  value={formData.expiresIn}
                  onChange={(e, { value }) =>
                    setFormData({ ...formData, expiresIn: value })
                  }
                  disabled={isSaving}
                >
                  <SimpleSelect.Option id="1" value="1">
                    1 Day
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="7" value="7">
                    7 Days
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="30" value="30">
                    30 Days
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="60" value="60">
                    60 Days
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="90" value="90">
                    90 Days
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="180" value="180">
                    180 Days
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="360" value="360">
                    360 Days
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="never" value="never">
                    Never
                  </SimpleSelect.Option>
                </SimpleSelect>

                <Flex gap="small" justifyItems="end">
                  <Button onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    color="primary"
                    disabled={isSaving}
                  >
                    {isSaving
                      ? 'Saving...'
                      : editingShareSid
                      ? 'Update'
                      : 'Create'}
                  </Button>
                </Flex>
              </Flex>
            </View>
          </Flex.Item>
        )}

        <Flex.Item>
          {shares.length === 0 ? (
            <View as="div" textAlign="center" padding="medium" background="secondary" borderRadius="medium">
              <Flex direction="column" gap="small" alignItems="center">
                <Text color="secondary">
                  No shares created yet. Share this session with others.
                </Text>
                {!showForm && (
                  <Button
                    renderIcon={IconAddLine}
                    onClick={handleCreate}
                    color="primary"
                    disabled={isSaving}
                  >
                    Create Share
                  </Button>
                )}
              </Flex>
            </View>
          ) : (
            <Flex direction="column" gap="small">
              {shares.map((share) => (
                <View
                  key={share.sid}
                  as="div"
                  background="secondary"
                  padding="small"
                  borderRadius="medium"
                  borderWidth="small"
                >
                  <Flex justifyItems="space-between" alignItems="start">
                    <Flex.Item shouldGrow shouldShrink>
                      <Text weight="bold" size="medium">{share.name}</Text>
                    </Flex.Item>
                    <Flex gap="x-small">
                      <IconButton
                        renderIcon={IconLinkLine}
                        screenReaderLabel="Generate magic link"
                        onClick={() => handleOpenMagicLink(share)}
                        size="small"
                        disabled={isSaving}
                      />
                      <IconButton
                        renderIcon={IconEditLine}
                        screenReaderLabel="Edit share"
                        onClick={() => handleEdit(share)}
                        size="small"
                        disabled={isSaving}
                      />
                      <IconButton
                        renderIcon={IconTrashLine}
                        screenReaderLabel="Delete share"
                        onClick={() => handleDelete(share.sid)}
                        size="small"
                        color="danger"
                        disabled={isSaving}
                      />
                    </Flex>
                  </Flex>

                  <View as="div" margin="x-small none none none">
                    <Flex direction="column" gap="xx-small">
                      <Text size="small" color="secondary">
                        <Text size="small" weight="bold">Emails: </Text>
                        {share.emails.join(', ')}
                      </Text>
                      {share.comments && (
                        <Text size="small" color="secondary">
                          <Text size="small" weight="bold">Comments: </Text>
                          {share.comments}
                        </Text>
                      )}
                      {share.createdByEmail && (
                        <Text size="small" color="secondary">
                          <Text size="small" weight="bold">Created by: </Text>
                          {share.createdByEmail}
                        </Text>
                      )}
                      <Flex gap="medium">
                        <Text size="small" color="secondary">
                          <Text size="small" weight="bold">Expires: </Text>
                          {share.expires && new Date(share.expires).getFullYear() < 2069
                            ? new Date(share.expires).toLocaleDateString()
                            : 'Never'}
                        </Text>
                        <Text size="small" color="secondary">
                          <Text size="small" weight="bold">Created: </Text>
                          {new Date(share.created).toLocaleDateString()}
                        </Text>
                      </Flex>
                    </Flex>
                  </View>
                </View>
              ))}
            </Flex>
          )}
        </Flex.Item>


      </Flex>

      {magicLinkShare && (
        <View
          as="div"
          position="fixed"
          insetBlockStart="0"
          insetInlineStart="0"
          width="100%"
          height="100%"
          background="primary"
          stacking="topmost"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999 }}
        >
          <Flex
            alignItems="center"
            justifyItems="center"
            height="100%"
            width="100%"
          >
            <View
              as="div"
              background="primary"
              padding="medium"
              borderRadius="large"
              shadow="above"
              maxWidth="500px"
              width="90%"
            >
              <Flex direction="column" gap="small">
                <Flex justifyItems="space-between" alignItems="center">
                  <Heading level="h4">Generate Magic Link</Heading>
                  <Button onClick={handleCloseMagicLink} size="small">
                    Close
                  </Button>
                </Flex>

                <Text size="small" color="secondary">
                  Generate a shareable link for <strong>{magicLinkShare.name}</strong>.
                  The recipient can use this link to access the session.
                </Text>

                <SimpleSelect
                  renderLabel="Recipient"
                  value={magicLinkEmail}
                  onChange={(e, { value }) => {
                    setMagicLinkEmail(value);
                    setMagicLinkUrl('');
                    setMagicLinkCopied(false);
                  }}
                  disabled={isGeneratingLink}
                >
                  {magicLinkShare.emails.map((email) => (
                    <SimpleSelect.Option
                      key={email}
                      id={email}
                      value={email}
                    >
                      {email}
                    </SimpleSelect.Option>
                  ))}
                </SimpleSelect>

                <SimpleSelect
                  renderLabel="Link Expires In"
                  value={magicLinkTtl}
                  onChange={(e, { value }) => {
                    setMagicLinkTtl(value);
                    setMagicLinkUrl('');
                    setMagicLinkCopied(false);
                  }}
                  disabled={isGeneratingLink}
                >
                  <SimpleSelect.Option id="ttl-15" value="15">
                    15 Minutes
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="ttl-30" value="30">
                    30 Minutes
                  </SimpleSelect.Option>
                  <SimpleSelect.Option id="ttl-60" value="60">
                    1 Hour
                  </SimpleSelect.Option>
                </SimpleSelect>

                {magicLinkError && (
                  <Alert variant="error" margin="none">
                    {magicLinkError}
                  </Alert>
                )}

                {!magicLinkUrl && (
                  <Button
                    onClick={handleGenerateMagicLink}
                    color="primary"
                    display="block"
                    disabled={isGeneratingLink}
                  >
                    {isGeneratingLink ? 'Generating...' : 'Generate Link'}
                  </Button>
                )}

                {magicLinkUrl && (
                  <View as="div" background="secondary" padding="small" borderRadius="medium">
                    <Flex direction="column" gap="small">
                      <Text size="small" weight="bold">Magic Link:</Text>
                      <View
                        as="div"
                        padding="x-small"
                        background="primary"
                        borderRadius="small"
                        borderWidth="small"
                        style={{ wordBreak: 'break-all', overflowWrap: 'anywhere', overflow: 'hidden', maxWidth: '100%' }}
                      >
                        <Text size="small" wrap="break-word">{magicLinkUrl}</Text>
                      </View>
                      <Button
                        renderIcon={IconCopyLine}
                        onClick={handleCopyMagicLink}
                        color={magicLinkCopied ? 'success' : 'primary'}
                        display="block"
                      >
                        {magicLinkCopied ? 'Copied!' : 'Copy to Clipboard'}
                      </Button>
                    </Flex>
                  </View>
                )}
              </Flex>
            </View>
          </Flex>
        </View>
      )}
    </View>
  );
};

export default Sharing;
